import React, { useState, useCallback } from 'react';
import { SelectBox, type SelectBoxTypes } from 'devextreme-react/select-box';
import type { CustomOperationEditorTemplate } from 'devextreme/ui/filter_builder';

import { Field } from './IFilterSelectionForm';

import TableApi from "./../../api/TableApi";
import FieldType from '../../consts/datasourcemodule/FieldType';
import FilteringCapabilitiesDto from '../../classes/dtos/FilteringCapabilitiesDto';
import GroupFilterDto from '../../classes/dtos/datasourcemodule/GroupFilterDto';
import FilterParameterVariableDto from '../../classes/dtos/datasourcemodule/FilterParameterVariableDto';
import FilterConstantValue from '../../classes/dtos/datasourcemodule/FilterConstantValue';

export interface CustomOperation {
    name: string,
    caption: string,
    icon: string,
    editorRender: (conditionInfo: CustomOperationEditorTemplate) => React.ReactNode,
    calculateFilterExpression: (filterValue: unknown, field: { dataField?: string }) => unknown[]
}

// Un paramètre défini utilisable comme opérande d'une condition "Variable". FieldType (null si non résolu,
// ex. paramètre Iteration dont le champ source n'est pas encore chargé) sert à n'exposer une condition
// paramétrable que pour les champs dont au moins un paramètre partage le type (voir buildFilteringFields
// et buildParameterOperations) : pas la peine de proposer un paramètre Texte sur un champ Entier.
// Name = libellé affiché dans la liste ; TechnicalName = ce qui est réellement stocké dans la condition
// (FilterParameterVariableDto.TechnicalName côté back).
export interface FilterParameterOption {
    Id: number;
    Name: string;
    TechnicalName: string;
    FieldType: number | null;
}

// Variables (conditions "Variable") référencées par le filtre courant : parcourt l'arbre GroupFilterDto,
// dédupliquées par TechnicalName. FieldType est résolu depuis le champ que porte la condition (resolveType
// optionnel : le panneau d'itération n'a pas besoin du type, seul le FilterBuilder le fournit).
export function collectParameterOptions(
    filter: GroupFilterDto | null,
    resolveType: (fieldReferenceId: number) => number | undefined = () => undefined
): FilterParameterOption[] {
    if (!filter) {
        return [];
    }

    const byTechnicalName = new Map<string, FilterParameterOption>();

    const visit = (group: GroupFilterDto): void => {
        group.Parameters.forEach((p) => {
            if (p instanceof FilterParameterVariableDto) {
                const technicalName = p.TechnicalName.trim();
                if (technicalName.length > 0 && !byTechnicalName.has(technicalName)) {
                    byTechnicalName.set(technicalName, {
                        Id: p.DataTableFieldParameterDefinition.Id,
                        Name: technicalName,
                        TechnicalName: technicalName,
                        FieldType: resolveType(p.FieldReferenceId) ?? null,
                    });
                }
            }
        });
        group.Childrens.forEach(visit);
    };

    visit(filter);
    return Array.from(byTechnicalName.values());
}

// Composant avec état local : le FilterBuilder ne réinvoque pas editorRender à chaque frappe,
// donc l'affichage doit être piloté par un state à lui plutôt que par conditionInfo.value (figé au premier rendu).
const ParameterEditor: React.FC<{ conditionInfo: CustomOperationEditorTemplate, parameterOptions: FilterParameterOption[] }> = ({ conditionInfo, parameterOptions }) => {
    const [value, setValue] = useState<string | null>((conditionInfo.value as string) ?? null);

    const onValueChanged = useCallback((e: SelectBoxTypes.ValueChangedEvent) => {
        const newValue = (e.value as string) ?? null;
        setValue(newValue);
        conditionInfo.setValue(newValue);
    }, [conditionInfo]);

    return (
        <SelectBox
            items={parameterOptions}
            displayExpr="Name"
            valueExpr="TechnicalName"
            value={value}
            placeholder="Choisir un paramètre"
            width="100%"
            onValueChanged={onValueChanged}
        />
    );
};

// Construit les 6 opérations "Paramètre" (suffixe "v") : chaque condition utilise un éditeur dédié
// (sélection d'un paramètre nommé) au lieu d'une constante. Leur libellé est suffixé "(paramètre)" pour
// les distinguer des opérations Constante homonymes dans le menu opérateur du FilterBuilder (les deux
// coexistent dans la même liste, voir buildFilteringFields). L'éditeur ne propose que les paramètres dont
// le type correspond à celui du champ de la condition en cours (résolu via fieldTypesByKey, à partir de
// conditionInfo.field.dataField qui vaut le FieldReferenceId du champ).
export function buildParameterOperations(parameters: FilterParameterOption[], fieldTypesByKey: Record<string, number>): CustomOperation[] {
    const optionsByType = new Map<number, FilterParameterOption[]>();
    // Variables dont le type n'a pas pu être résolu (ex. déclarées sur une colonne de mapping dont le
    // champ source n'expose pas de capacités de filtre) : proposées sur TOUS les champs plutôt que
    // masquées — l'utilisateur les a déclarées explicitement, à lui de juger la compatibilité.
    const untypedOptions: FilterParameterOption[] = [];
    parameters.forEach((p) => {
        if (p.FieldType == null) {
            untypedOptions.push(p);
            return;
        }
        const options = optionsByType.get(p.FieldType) ?? [];
        options.push(p);
        optionsByType.set(p.FieldType, options);
    });

    const renderParameterEditor = (conditionInfo: CustomOperationEditorTemplate) => {
        const fieldType = fieldTypesByKey[conditionInfo.field.dataField ?? ''];
        const typedOptions = (fieldType !== undefined ? optionsByType.get(fieldType) : undefined) ?? [];
        const parameterOptions = untypedOptions.length > 0 ? [...typedOptions, ...untypedOptions] : typedOptions;
        return <ParameterEditor conditionInfo={conditionInfo} parameterOptions={parameterOptions} />;
    };

    return [
        { name: "<v", caption: "Inférieur à (paramètre)", icon: "less", editorRender: renderParameterEditor, calculateFilterExpression: (filterValue, field) => [field.dataField, "<", filterValue] },
        { name: ">v", caption: "Supérieur à (paramètre)", icon: "greater", editorRender: renderParameterEditor, calculateFilterExpression: (filterValue, field) => [field.dataField, ">", filterValue] },
        { name: "<=v", caption: "Inférieur ou égal à (paramètre)", icon: "lessorequal", editorRender: renderParameterEditor, calculateFilterExpression: (filterValue, field) => [field.dataField, "<=", filterValue] },
        { name: ">=v", caption: "Supérieur ou égal à (paramètre)", icon: "greaterorequal", editorRender: renderParameterEditor, calculateFilterExpression: (filterValue, field) => [field.dataField, ">=", filterValue] },
        { name: "=v", caption: "Égal à (paramètre)", icon: "equal", editorRender: renderParameterEditor, calculateFilterExpression: (filterValue, field) => [field.dataField, "=", filterValue] },
        { name: "<>v", caption: "Différent de (paramètre)", icon: "notequal", editorRender: renderParameterEditor, calculateFilterExpression: (filterValue, field) => [field.dataField, "<>", filterValue] },
    ];
}

// Captions françaises des opérateurs natifs, utilisées en Mode="Constant".
export const CONSTANT_OPERATION_DESCRIPTIONS = {
    lessThan: "Inférieur à",
    greaterThan: "Supérieur à",
    lessThanOrEqual: "Inférieur ou égal à",
    greaterThanOrEqual: "Supérieur ou égal à",
    equal: "Égal à",
    notEqual: "Différent de",
};

// Un seul bit-mask d'opérateurs disponibles côté backend ; chaque Mode n'en expose qu'une variante
// (native pour Constant, suffixée "v" pour Parameter) au FilterBuilder.
export const getConstantFilterOperations = (operators: number): string[] => {
    let result: string[] = [];

    if ((operators & 1) === 1) { result.push("<"); }
    if ((operators & 2) === 2) { result.push(">"); }
    if ((operators & 4) === 4) { result.push("<="); }
    if ((operators & 8) === 8) { result.push(">="); }
    if ((operators & 16) === 16) { result.push("="); }
    if ((operators & 32) === 32) { result.push("<>"); }

    return result;
};

export const getParameterFilterOperations = (operators: number): string[] => {
    let result: string[] = [];

    if ((operators & 1) === 1) { result.push("<v"); }
    if ((operators & 2) === 2) { result.push(">v"); }
    if ((operators & 4) === 4) { result.push("<=v"); }
    if ((operators & 8) === 8) { result.push(">=v"); }
    if ((operators & 16) === 16) { result.push("=v"); }
    if ((operators & 32) === 32) { result.push("<>v"); }

    return result;
};

// Chargement réseau seul (capacités brutes) : séparé de buildFilteringFields ci-dessous pour permettre de
// reconstruire les Field (et leurs opérations "Paramètre" disponibles) à chaque changement de la liste des
// paramètres définis, sans réémettre d'appel API à chaque fois.
export async function loadFilteringCapabilities(tableReferenceId: number): Promise<{ capabilities: FilteringCapabilitiesDto[]; fieldTypesByKey: Record<string, number> }> {
    const getFilteringCapabilitiesResult = await TableApi.GetFilteringCapabilitiesAsync(tableReferenceId);

    const capabilities = getFilteringCapabilitiesResult.Result.FilteringCapabilities;

    // Clé = FieldReferenceId (string), pas le nom du champ : c'est ce que le back attend
    // désormais dans FilterParameterModel.FieldReferenceId (voir FilterConverter).
    const fieldTypesByKey: Record<string, number> = {};
    capabilities.forEach((e) => {
        fieldTypesByKey[e.Id.toString()] = e.Type;
    });

    return { capabilities, fieldTypesByKey };
}

function buildField(e: FilteringCapabilitiesDto, getFilterOperations: (operators: number) => string[]): Field {
    const dataField = e.Id.toString();

    switch (e.Type) {

        case FieldType.Int32:
            return { dataField, dataType: "number", caption: e.Label, filterOperations: getFilterOperations(e.Operators), editorOptions: { format: "#0" } } as Field;

        case FieldType.Decimal:
        case FieldType.Double:
            return { dataField, dataType: "number", caption: e.Label, filterOperations: getFilterOperations(e.Operators) } as Field;

        // customizeText : la pastille d'une condition date est rendue au format régional du navigateur
        // (voir FilterConstantValue.toDisplayText), indépendamment de la config de localisation DevExtreme.
        case FieldType.Date:
            return {
                dataField, dataType: "date", caption: e.Label, filterOperations: getFilterOperations(e.Operators), format: "shortDate",
                customizeText: (info) => FilterConstantValue.fromInput(info.value, FieldType.Date).toDisplayText(),
            } as Field;

        case FieldType.DateTime:
        case FieldType.DateTimeOffset:
            return {
                dataField, dataType: "datetime", caption: e.Label, filterOperations: getFilterOperations(e.Operators), format: "shortDateShortTime",
                customizeText: (info) => FilterConstantValue.fromInput(info.value, e.Type).toDisplayText(),
            } as Field;

        case FieldType.String:
        default:
            return { dataField, dataType: "string", caption: e.Label, filterOperations: getFilterOperations(e.Operators) } as Field;

    }
}

// Construit les Field DevExtreme à partir de capacités déjà chargées (voir loadFilteringCapabilities),
// avec les opérateurs natifs ET paramétrés (suffixe "v") réunis dans la même liste — un seul FilterBuilder
// permet ainsi de mélanger des conditions Constante et Paramètre sur un même champ, sans avoir à choisir un
// mode au préalable. Les opérations "Paramètre" ne sont toutefois ajoutées que pour les champs dont le type
// a au moins un paramètre défini (typesWithParameter) : un champ sans paramètre compatible ne montre donc
// que ses opérations Constante, plutôt qu'une condition paramétrable qu'aucun paramètre ne pourrait satisfaire.
// usedOperationsByField : opérations déjà présentes dans le filtre enregistré, par champ (voir
// collectOperationsByField). Toute opération qu'une condition existante utilise est réinjectée dans le
// `filterOperations` du champ même si la règle ci-dessus ne l'aurait pas proposée — sinon le
// FilterBuilder lève E1048 en affichant cette condition (typiquement un "=v" sans paramètre défini).
export function buildFilteringFields(
    capabilities: FilteringCapabilitiesDto[],
    parameters: FilterParameterOption[],
    usedOperationsByField: Map<string, Set<string>> = new Map()
): Field[] {
    const typesWithParameter = new Set(parameters.map((p) => p.FieldType).filter((t): t is number => t != null));
    // Au moins une variable au type non résolu : on propose les opérations "Paramètre" sur tous les
    // champs (voir buildParameterOperations), sinon elle serait déclarée mais inutilisable partout.
    const hasUntypedParameter = parameters.some((p) => p.FieldType == null);

    return capabilities.map((e) => buildField(e, (operators) => {
        const constantOps = getConstantFilterOperations(operators);
        const ops = (typesWithParameter.has(e.Type) || hasUntypedParameter)
            ? [...constantOps, ...getParameterFilterOperations(operators)]
            : constantOps;

        const used = usedOperationsByField.get(e.Id.toString());
        if (used) {
            used.forEach((op) => {
                if (!ops.includes(op)) {
                    ops.push(op);
                }
            });
        }

        return ops;
    }));
}
